import { useCallback, useMemo } from 'react';

import { useSessionStorageValueAsync } from '#src/app/shared/hooks/storage';

import { INFERENCE_MATCH_METHOD, type ConstituentIdentification } from '../scopeMatch';

const keyFor = (petitionId: string): string => `constituent-status-${petitionId}`;

function parse(raw: string | null | undefined): ConstituentIdentification | null {
	if (!raw) return null;
	try {
		const parsed = JSON.parse(raw) as Partial<ConstituentIdentification>;
		if (typeof parsed.isConstituent !== 'boolean' || parsed.method !== INFERENCE_MATCH_METHOD) return null;
		return { isConstituent: parsed.isConstituent, method: INFERENCE_MATCH_METHOD };
	} catch {
		return null;
	}
}

type UseConstituentStatus = readonly [
	ConstituentIdentification | null,
	(value: ConstituentIdentification | null) => void,
];

/**
 * Carries the constituent identification result across the sign -> combo-screen navigation: the
 * decision is made at sign time (where the signer's submitted address is available) and read on the
 * combo screen (a fresh page load after the sign redirect) to gate experiment exposure.
 *
 * It lives in sessionStorage, keyed per petition, because it is throwaway per-tab state, not a record
 * of truth: a tampered value only lets a curious user into a variant, which is acceptable here.
 *
 * Uses the async storage hook so it is SSR-safe (the sync variant throws on the server); the value is
 * undefined until the client resolves it.
 */
export function useConstituentStatus(petitionId: string): UseConstituentStatus {
	const [raw, setRaw] = useSessionStorageValueAsync(keyFor(petitionId));

	const value = useMemo(() => parse(raw), [raw]);

	const setValue = useCallback(
		(next: ConstituentIdentification | null): void => {
			setRaw(next === null ? null : JSON.stringify(next));
		},
		[setRaw],
	);

	return [value, setValue];
}
